/**
 * Debug Page - Test Bitcoin.com React CL Components
 */

import { Button } from '@bitcoin-com/react-components/src/components/button'
import { Card } from '@bitcoin-com/react-components/src/components/card'

export function DebugPage() {
  return (
    <div className="min-h-screen bg-background p-8">
      <div className="max-w-4xl mx-auto space-y-8">
        
        {/* Debug Info */}
        <div className="bg-shades-white p-4 rounded border-2 border-shades-black">
          <h1 className="text-2xl font-bold mb-4">DEBUG PAGE - Bitcoin.com React CL</h1>
          <pre className="text-xs bg-shades-extra-light p-4 rounded overflow-auto">
{`Tailwind Config:
- Content includes: node_modules/@bitcoin-com/react-components/src/**/*.{js,ts,jsx,tsx}
- Colors: primary-100, secondary-100, shades-*, etc.
- Spacing: xs, s, m, l, xl, xxl
- Fonts: Satoshi, Elza, IBMPlexSans

Import Path:
import { Button } from '@bitcoin-com/react-components/src/components/button'

Expected Button Classes:
- rounded-[40px] (pill shape)
- bg-primary-100 (blue)
- px-m py-s (padding)
- font-sans font-medium
`}
          </pre>
        </div>

        {/* Test: Raw Tailwind Classes */}
        <Card className="p-m">
          <h2 className="text-xl font-bold mb-4">Test 1: Raw Tailwind Classes</h2>
          
          <div className="space-y-4">
            {/* Test basic colors */}
            <div>
              <p className="text-sm mb-2">Basic Colors:</p>
              <div className="flex gap-2">
                <div className="w-20 h-20 bg-primary-100 rounded" title="primary-100"></div>
                <div className="w-20 h-20 bg-secondary-100 rounded" title="secondary-100"></div>
                <div className="w-20 h-20 bg-success-100 rounded" title="success-100"></div>
              </div>
              <p className="text-xs mt-1 text-shades-semi-dark">
                Should see: Blue (#4169e1), Orange (#ff9500), Green (#2fb790)
              </p>
            </div>

            {/* Test spacing */}
            <div>
              <p className="text-sm mb-2">Spacing Test:</p>
              <div className="bg-shades-extra-light p-m rounded">
                <div className="bg-shades-white p-s">
                  p-m (16px) outer, p-s (8px) inner
                </div>
              </div>
            </div>

            {/* Test border radius */}
            <div>
              <p className="text-sm mb-2">Border Radius Test:</p>
              <div className="flex gap-2">
                <div className="w-20 h-10 bg-shades-dark rounded-xs"></div>
                <div className="w-20 h-10 bg-shades-dark rounded-sm"></div>
                <div className="w-20 h-10 bg-shades-dark rounded-md"></div>
                <div className="w-20 h-10 bg-shades-dark rounded-[40px]"></div>
              </div>
              <p className="text-xs mt-1 text-shades-semi-dark">
                xs (4px), sm (8px), md (16px), [40px] (pill)
              </p>
            </div>
          </div>
        </Card>

        {/* Test: Library Button Component */}
        <Card className="p-m">
          <h2 className="text-xl font-bold mb-4">Test 2: Library Button Component</h2>
          
          <div className="space-y-4">
            <div>
              <p className="text-sm mb-2">Button from Library:</p>
              <div className="flex gap-2 flex-wrap">
                <Button variant="primary" size="sm">Primary Small</Button>
                <Button variant="primary" size="md">Primary Medium</Button>
                <Button variant="primary" size="lg">Primary Large</Button>
              </div>
              <p className="text-xs mt-2 text-shades-semi-dark">
                Expected: Pill-shaped, blue background, white text
              </p>
            </div>

            <div>
              <p className="text-sm mb-2">Secondary Buttons:</p>
              <div className="flex gap-2 flex-wrap">
                <Button variant="secondary" size="sm">Secondary Small</Button>
                <Button variant="secondary" size="md">Secondary Medium</Button>
                <Button variant="secondary" size="lg">Secondary Large</Button>
              </div>
              <p className="text-xs mt-2 text-shades-semi-dark">
                Expected: Pill-shaped, orange background, white text
              </p>
            </div>

            <div>
              <p className="text-sm mb-2">Other Variants:</p>
              <div className="flex gap-2 flex-wrap">
                <Button variant="default" size="md">Default</Button>
                <Button variant="text" size="md">Text</Button>
                <Button variant="link" size="md">Link</Button>
                <Button variant="strong" size="md">Strong</Button>
              </div>
            </div>
          </div>
        </Card>

        {/* Test: Inspect Element */}
        <Card className="p-m">
          <h2 className="text-xl font-bold mb-4">Test 3: Inspect This Button</h2>
          
          <Button 
            variant="primary" 
            size="md"
            className="inspect-me"
            id="inspect-button"
          >
            Inspect Me in DevTools
          </Button>

          <div className="mt-4 text-sm">
            <p className="font-bold mb-2">Instructions:</p>
            <ol className="list-decimal list-inside space-y-1 text-shades-semi-dark">
              <li>Right-click the button above</li>
              <li>Select "Inspect Element"</li>
              <li>Look at the classes in DevTools</li>
              <li>Check if classes like <code className="bg-shades-extra-light px-1">rounded-[40px]</code> are present</li>
              <li>Check if <code className="bg-shades-extra-light px-1">bg-primary-100</code> is present</li>
              <li>Check computed styles for actual color values</li>
            </ol>
          </div>
        </Card>

        {/* Test: Manual Pill Button */}
        <Card className="p-m">
          <h2 className="text-xl font-bold mb-4">Test 4: Manual Recreation</h2>
          
          <div className="space-y-4">
            <p className="text-sm">Button manually recreated with same classes:</p>
            
            <button className="relative inline-flex items-center justify-center font-sans font-medium rounded-[40px] transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:pointer-events-none bg-primary-100 text-white hover:bg-primary-50 focus-visible:ring-primary-100/40 focus-visible:ring-offset-shades-off-white disabled:bg-shades-semi-light disabled:text-white/70 gap-s px-m py-s min-h-[2.1875rem]">
              <span className="truncate text-label">Manual Button</span>
            </button>

            <p className="text-xs text-shades-semi-dark">
              If this looks correct but library button doesn't, there's an import issue.
              <br />
              If neither looks correct, Tailwind isn't processing the classes.
            </p>
          </div>
        </Card>

        {/* Console Debug Output */}
        <Card className="p-m">
          <h2 className="text-xl font-bold mb-4">Console Debug Output</h2>
          <button
            className="bg-shades-black text-white px-m py-s rounded-md"
            onClick={() => {
              console.group('🔍 Bitcoin.com React CL Debug Info')
              
              console.log('1. Component Import Check:')
              console.log('   Button imported from:', '@bitcoin-com/react-components/src/components/button')
              console.log('   Button type:', typeof Button)
              
              console.log('\n2. Tailwind Classes Check:')
              const btn = document.getElementById('inspect-button')
              if (btn) {
                console.log('   Button classes:', btn.className)
                const styles = window.getComputedStyle(btn)
                console.log('   Computed background:', styles.backgroundColor)
                console.log('   Computed border-radius:', styles.borderRadius)
                console.log('   Computed padding:', styles.padding)
              }
              
              console.log('\n3. CSS Variables Check:')
              const root = document.documentElement
              const rootStyles = window.getComputedStyle(root)
              console.log('   --color-primary-100:', rootStyles.getPropertyValue('--color-primary-100'))
              console.log('   --color-secondary-100:', rootStyles.getPropertyValue('--color-secondary-100'))
              console.log('   --color-background:', rootStyles.getPropertyValue('--color-background'))
              
              console.log('\n4. Font Check:')
              const body = document.body
              const bodyStyles = window.getComputedStyle(body)
              console.log('   Body font-family:', bodyStyles.fontFamily)
              
              console.log('\n5. Tailwind Test:')
              const testDiv = document.createElement('div')
              testDiv.className = 'bg-primary-100 rounded-[40px] px-m py-s'
              document.body.appendChild(testDiv)
              const testStyles = window.getComputedStyle(testDiv)
              console.log('   Test div background:', testStyles.backgroundColor)
              console.log('   Test div border-radius:', testStyles.borderRadius)
              console.log('   Test div padding:', testStyles.padding)
              document.body.removeChild(testDiv)
              
              console.groupEnd()
            }}
          >
            Run Console Debug
          </button>
          <p className="text-xs mt-2 text-shades-semi-dark">
            Click button above and check browser console (F12) for detailed debug info
          </p>
        </Card>

      </div>
    </div>
  )
}

